<script setup>
import { ref, computed, getCurrentInstance, onMounted, onUnmounted } from 'vue'
import { $config, defaultServiceOptions } from '@/common/config'
import { $router } from '@/router'
import { $bus, mqtt } from '@/common/events'
import { nanoid } from '@/common/helper'
import { dialog } from '@/common/dialog'
import { hcu } from '@/common/hcu'
import { env } from '@/secrets/env'
import { useAuthState } from '@/composables/auth'
import { useTitle } from '@/composables/title'
import { useMenu, useSubmenu, useTopBar } from '@/composables/menu'
import { useSmartCard, onSmartCardReceived } from '@/composables/hardware/smartcard'
import { useTranslate } from '@/composables/language'
import { canInterrupt } from '@/composables/interrupt'
import { useSessionTimer } from '@/composables/session'
import { AuthService } from '@/services/AuthService.js'

const authService = new AuthService(defaultServiceOptions());

const { t, translateError } = useTranslate();
const instance = getCurrentInstance();
const authStore = useAuthState();
const { smartcard } = useSmartCard();

const message = ref(null);

//Redirect to service window
function goToServiceWindow() {
  message.value = t('intro.redirecting');
  $router.push({ name: "ServiceWindow", query: { service: "true" } });
}

onMounted(() => {
  useTitle("Vzdrževanje");
  useMenu(false);
  useSubmenu(false);
  useTopBar(false);

  //Do not allow any external event interruptions (such as id card scanning)
  canInterrupt(false);

  //Avoid session timeout while a tray is open
  useSessionTimer(false);
});

onUnmounted(() => {
  //Restore interruptions
  canInterrupt(true);
});

onSmartCardReceived(async (data) => {
  try {
    let code = data.code;
  
    //Must be of string type
    if (typeof code == "number") {
      code = code.toString();
    }

    //Special local RFID code to enter service window
    console.log(code, env.VITE_SERVICE_RFID_TOKEN);
    if (code == env.VITE_SERVICE_RFID_TOKEN) {
      goToServiceWindow();
      return
    }

    //Get user's permission without actually logging in - no session is being created on "preflight"
    let result = await authService.login({ rfid125: code, preflight: true });
    if (result.error) {
      throw new Error(result.error);
    }

    //Check if user is allowed to enter service window
    let permissions = result.user.permissions;
    let allowed = permissions.includes("cabinet.admin") || permissions.includes("cabinet.service");

    //Jump to service window if allowed
    if (allowed) {
      //Perform actual log in
      result = await authService.login({ rfid125: code });
      if (result.error) {
        throw new Error(result.error);
      }

      $config.sessionId = result.session.id;

      //Logged in
      authStore.context.session = result.session;
      authStore.context.user = result.user;
      authStore.context.jwt = result.jwt;
      authStore.context.permissions = result.user?.permissions || [];
      authStore.save();

      $bus.emit("login", { displayName: result.user?.displayName, username: result.user?.username });

      goToServiceWindow();
    }
  }
  catch (err) {
    console.error("[intro] onSmartCardReceived", err);
  }
});
</script>

<template>

  <!-- Maintenance message -->
  <div class="screen-container">
    <div class="text-center">
      <i class="fa fa-exclamation-triangle text-amber-400 huge-icon"></i>
      <div class="mt-5 text-lg font-bold">{{ $t('maintenance.title2') }}</div>
      <div class="mt-5">{{ $t('maintenance.message2') }}</div>
    </div>
  </div>

</template>

<style scoped>
.screen-container {
  @apply grid min-h-screen place-items-center dark:bg-gray-800 dark:text-white;
}

.huge-icon {
  font-size: 210px;
}
</style>
